import { useEffect, useMemo, forwardRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { client } from 'utilities/db';
import { useFetchVendorsHook } from 'hooks/vendors/useFetchVendorsHook';
import { useFetchProductsHook } from 'hooks/products/useFetchProductsHook';
import { useFetchProductsCategoryHook } from 'hooks/products/useFetchProductsCategoryHook';
import { useFetchProductsGroupHook } from 'hooks/products/useFetchProductsGroupHook';
import { useFetchInventoryHook } from 'hooks/inventory/useFetchInventoryHook';
import { CustomSelectDropdown } from 'components/select/multi';
import { TextField } from 'components/input';
import { Button } from 'components/button';
import { Toasts } from 'components/toasts';
import { useStore } from 'store';
import styles from './index.module.css';
import { useNavigate } from 'react-router-dom';
import CurrencyInput from 'react-currency-input-field';
import { useCountry } from 'contexts/CountryContext';
import { DatePicker } from 'rsuite';
import { ModalComponent } from 'components/modal';

// Wrapper components with proper ref forwarding
const DatePickerWithRef = forwardRef((props, ref) => (
  <DatePicker {...props} ref={ref} />
));
DatePickerWithRef.displayName = 'DatePickerWithRef';

const CurrencyInputWithRef = forwardRef((props, ref) => (
  <CurrencyInput {...props} ref={ref} />
));
CurrencyInputWithRef.displayName = 'CurrencyInputWithRef';

export const ManualInventoryPage = () => {
  const { brand, branch } = useStore((state) => state);
  const { currencySymbol, dateFormat } = useCountry();
  const pickerFormat = dateFormat === 'DD/MM/YYYY' ? 'dd/MM/yyyy' : dateFormat === 'DD-MM-YYYY' ? 'dd-MM-yyyy' : 'MM/dd/yyyy';
  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    watch
  } = useForm({
    shouldFocusError: false, // Disable automatic focus to prevent errors with third-party components
    defaultValues: {
      vendor: '',
      product: '',
      category: '',
      group: '',
      quantity: '',
      unit: '',
      date_of_purchase: '',
      date_of_expiry: '',
      // price_per_unit: '',
      total_price: '',
      notes: '',
      brand: '',
      branch: '',
      low_stock_alert: ''
    }
  });

  const { productsData, productsLoading } = useFetchProductsHook(
    `products/${brand?.id}/${branch?.id}`,
    brand?.id,
    branch?.id
  );

  const { inventoryRefetch } = useFetchInventoryHook(
    `inventory/${brand?.id}`,
    branch?.id
  );

  const { mutate, isError, error, isSuccess } = useMutation((invData) => {
    return client.post('/inventory/add', invData);
  });

  const [formData, setFormData] = useState(null);
  const [showNegativeInventoryModal, setShowNegativeInventoryModal] =
    useState(false);
  const [negativeInventoryResponse, setNegativeInventoryResponse] =
    useState(null);
  console.log(
    '🚀 ~ ManualInventoryPage ~ showNegativeInventoryModal:',
    showNegativeInventoryModal
  );

  const [userAcceptsNegativeInventory, setUserAcceptsNegativeInventory] =
    useState(false);

  const handleAddManualInventory = async (data) => {
    try {
      // show modal if negative inventory found.
      const negativeInventoryResponse = (
        await client.post(`/inventory/checkNegativeInventory`, {
          product_id: data?.product?.value
        })
      )?.data;
      setNegativeInventoryResponse(negativeInventoryResponse);

      const negativeInventory = negativeInventoryResponse?.negative;

      console.log(
        '🚀 ~ handleAddManualInventory ~ negativeInventory:',
        negativeInventory
      );

      setFormData(data);
      if (true) {
        setShowNegativeInventoryModal(true);
        return;
      } else {
        // No negative inventory, proceed directly
        setUserAcceptsNegativeInventory(false);
        handleSubmitManualInventory(data);
      }
    } catch (error) {
      console.log('🚀 ~ handleAddManualInventory ~ error:', error);
      Toasts.error(error?.response?.data?.message || 'An error occurred');
    }
  };

  const handleInventoryAction = (action) => {
    setShowNegativeInventoryModal(false);

    if (action === 'cancel') {
      inventoryRefetch();
      navigate(`/inventory/listing`);
      return; // Just close the modal, don't proceed
    }

    // Set the deplete flag based on the action
    const deplete = action === 'clear_deficit';
    setUserAcceptsNegativeInventory(deplete);
    handleSubmitManualInventory(null, deplete);
  };

  const handleSubmitManualInventory = async (data, deplete = false) => {
    const payload = {
      branch_id: +branch?.id,
      product_id: data?.product?.value || formData?.product?.value,
      quantity: Number(data?.quantity || formData?.quantity),
      low_stock_alert: data?.low_stock_alert || formData?.low_stock_alert || 0,
      unit: data?.unit?.value || formData?.unit?.value,
      total_price: Number(data?.total_price || formData?.total_price),
      purchase_date: data?.date_of_purchase || formData?.date_of_purchase,
      expiry_date: data?.date_of_expiry || formData?.date_of_expiry,
      notes: data?.notes || formData?.notes || '',
      deplete: deplete
    };

    // Add extra key if user accepts negative inventory (for backward compatibility)
    if (userAcceptsNegativeInventory) {
      payload.allow_negative_inventory = true;
    }

    mutate(payload);
  };

  useMemo(() => {
    if (isSuccess) {
      Toasts.success('Inventory added successfully');
      inventoryRefetch();
      navigate(`/inventory/listing`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (!isError) return;
    Toasts.error(
      error?.response?.data?.message?.[1]?.msg || error?.response?.data?.message
    );
  }, [isError, error]);

  return (
    <>
      <form onSubmit={handleSubmit(handleAddManualInventory)}>
        <div className={styles.bx}>
          <div className={styles.row}>
            <div>
              <Controller
                name="product"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <CustomSelectDropdown
                    hookForm={true}
                    field={field}
                    label="Product *"
                    placeholder="Choose options"
                    options={productsData?.map((prod) => ({
                      value: prod?.id,
                      label: prod?.product_name || '-'
                    }))}
                    error={
                      errors?.product?.type === 'required'
                        ? 'Product is required'
                        : ''
                    }
                    isLoading={productsLoading}
                  />
                )}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div>
              <TextField
                label="Quantity *"
                placeholder="Quantity"
                name="quantity"
                error={
                  errors?.quantity?.type === 'required'
                    ? 'Quantity is required'
                    : ''
                }
                register={register}
                validation={{
                  required: true
                }}
              />
            </div>
            <div>
              <TextField
                label="Low stock alert *"
                placeholder="Low stock alert*"
                name="low_stock_alert"
                error={
                  errors?.low_stock_alert?.type === 'required'
                    ? 'Low stock alert is required'
                    : errors?.low_stock_alert?.type === 'pattern'
                    ? 'Low stock alert must be a number'
                    : ''
                }
                register={register}
                validation={{
                  required: true,
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Low stock alert must be a number'
                  }
                }}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div>
              <Controller
                name="unit"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <CustomSelectDropdown
                    hookForm={true}
                    field={field}
                    label="Unit *"
                    placeholder="Choose options"
                    options={[
                      'kg',
                      'pounds',
                      'grams',
                      'ounces',
                      'liters',
                      'teaspoon',
                      'number/pieces',
                      'tablespoon',
                      'dozen'
                    ]?.map((unit) => ({
                      value: unit,
                      label: unit?.toUpperCase() || '-'
                    }))}
                    error={
                      errors?.unit?.type === 'required'
                        ? 'Unit is required'
                        : ''
                    }
                    //isLoading={brandsLoading}
                  />
                )}
              />
            </div>
            <div>
              {/* <TextField
                label="Date of purchase *"
                placeholder="mm/dd/yyyy"
                name="date_of_purchase"
                error={
                  errors?.unit?.type === 'required'
                    ? 'Date of purchase is required'
                    : ''
                }
                register={register}
                validation={{
                  required: true
                }}
                type="date"
                format="mm/dd/yyyy"
              /> */}
              <Controller
                name="date_of_purchase"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div
                    style={{
                      width: '100%',
                      // marginTop: '10px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '12px',
                        gap: '10px',
                        borderRadius: '10px'
                      }}
                    >
                      <label
                        style={{
                          marginLeft: '10px',
                          fontSize: '12px',
                          fontWeight: '400'
                        }}
                      >
                        Date of purchase
                      </label>
                      <DatePickerWithRef
                        {...field}
                        format={pickerFormat}
                        style={{ width: '100%' }}
                      />
                    </div>
                    {errors?.date_of_purchase?.type === 'required' && (
                      <span
                        style={{
                          color: 'red',
                          fontSize: '11px',
                          marginTop: '5px'
                        }}
                      >
                        Date of purchase is required
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div>
              {/* <TextField
                label="Date of expiry *"
                placeholder="mm/dd/yyyy"
                name="date_of_expiry"
                error={
                  errors?.date_of_expiry?.type === 'required'
                    ? 'Date of expiry is required'
                    : ''
                }
                register={register}
                validation={{
                  required: true
                }}
                type="date"
              /> */}

              <Controller
                name="date_of_expiry"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div
                    style={{
                      width: '100%',
                      // marginTop: '10px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '16px',
                        // gap: '15px',
                        borderRadius: '10px'
                      }}
                    >
                      <label
                        style={{
                          marginLeft: '10px',
                          fontSize: '12px',
                          fontWeight: '400',
                          marginBottom: '10px'
                        }}
                      >
                        Date of expiry
                      </label>
                      <DatePickerWithRef
                        {...field}
                        format={pickerFormat}
                        style={{ width: '100%' }}
                      />
                    </div>
                    {errors?.date_of_expiry?.type === 'required' && (
                      <span
                        style={{
                          color: 'red',
                          fontSize: '11px',
                          marginTop: '5px'
                        }}
                      >
                        Date of expiry is required
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
            <div>
              <Controller
                name="total_price"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <>
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '10px'
                      }}
                    >
                      <label
                        style={{
                          marginLeft: '10px',
                          fontSize: '12px',
                          fontWeight: '400'
                        }}
                      >
                        Total Price
                      </label>
                      <CurrencyInputWithRef
                        id="validation-example-2-field"
                        placeholder={`${currencySymbol}10`}
                        allowDecimals={true}
                        className={`form-control`}
                        onValueChange={(val) => {
                          console.log('🚀 ~ onValueChange ~ val:', val);
                          field.onChange(val);
                        }}
                        prefix={currencySymbol}
                        step={10}
                        style={{
                          width: '100%',
                          padding: '15px',
                          borderRadius: '5px',
                          border: '0px solid #ccc'
                        }}
                      />
                    </div>
                    {errors?.total_price?.type === 'required' && (
                      <span
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '8px'
                        }}
                      >
                        Total price is required
                      </span>
                    )}
                  </>
                )}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div>
              <TextField
                label="Notes (optional)"
                placeholder="Notes"
                name="notes"
                error={
                  errors?.notes?.type === 'required' ? 'Notes is required' : ''
                }
                register={register}
                validation={{}}
              />
            </div>
            {/* <div>
              <Controller
                name="branch"
                control={control}
                // rules={{ required: true }}
                render={({ field }) => (
                  <CustomSelectDropdown
                    hookForm={true}
                    field={field}
                    label="Branch (optional Select if inventory is at branch level else leave blank)"
                    placeholder="Choose options"
                    options={[
                      {
                        value: branch?.id,
                        label:
                          branch?.branch_name || branch?.kitchen_name || '-'
                      }
                    ]}
                    error={
                      errors?.branch?.type === 'required'
                        ? 'Branch is required'
                        : ''
                    }
                    //isLoading={branchesLoading}
                  />
                )}
              />
            </div> */}
          </div>
          {/* <div className={styles.row}>
            <div>
              <Controller
                name="brand"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <CustomSelectDropdown
                    hookForm={true}
                    field={field}
                    label="Brand *"
                    placeholder="Choose options"
                    options={[
                      {
                        value: brand?.id,
                        label: brand?.brand_name || '-'
                      }
                    ]}
                    error={
                      errors?.brand?.type === 'required'
                        ? 'Brand is required'
                        : ''
                    }
                    //isLoading={brandsLoading}
                  />
                )}
              />
            </div>
            <div></div>
          </div> */}
          <div className={styles.footer}>
            <Button
              onClick={() => {
                navigate(`/inventory/listing`);
              }}
              color="outline-lite"
              label={'Cancel'}
            />
            <Button label="Submit" color="bg-primary" type="submit" />
          </div>
        </div>
      </form>

      {/* Negative Inventory Modal */}
      {showNegativeInventoryModal && (
        <ModalComponent
          modal={showNegativeInventoryModal}
          handleModal={() => setShowNegativeInventoryModal(false)}
        >
          <div
            style={{
              maxWidth: '500px',
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '10px',
                // maxWidth: '500px',
                width: '90%',
                textAlign: 'center'
              }}
            >
              <h3 style={{ marginBottom: '20px', color: '#333' }}>
                Negative Inventory Warning
              </h3>
              <p
                style={{
                  marginBottom: '30px',
                  color: '#666',
                  lineHeight: '1.5'
                }}
              >
                This item currently shows a negative on-hand balance of -
                {negativeInventoryResponse?.quantity} grams. Do you want to
                apply the incoming {formData?.quantity} {formData?.unit?.value}{' '}
                to clear the deficit first? Any remainder will be added to
                on-hand.
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: '15px',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <Button
                  label="Clear Deficit First (recommended)"
                  color="bg-primary"
                  onClick={() => handleInventoryAction('clear_deficit')}
                  style={{ minWidth: '200px' }}
                />
                <Button
                  label="Add as New Stock Only"
                  color="outline-lite"
                  onClick={() => handleInventoryAction('add_new_stock')}
                  style={{ minWidth: '200px' }}
                />
                <Button
                  label="Cancel"
                  color="outline-lite"
                  onClick={() => handleInventoryAction('cancel')}
                  style={{ minWidth: '200px' }}
                />
              </div>
            </div>
          </div>
        </ModalComponent>
      )}
    </>
  );
};
